import { Component, computed, effect, input, output } from '@angular/core';
import { FormControl } from '@angular/forms';

import { MomentModule } from 'ngx-moment';

import { FnCallPipe, NgTemplateAssertType } from '@core/common-legacy';
import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import type { BackgroundToken } from '@core/theme-util';
import {
  AvatarComponent,
  ButtonComponent,
  ButtonDropdownIndicatorDirective,
  ConnectedOverlayDirective,
  DropdownComponent,
  DropdownItemComponent,
  IconComponent,
  type IconName,
  type IconToken,
  InlineOpenStrategy,
  OverlayOriginDirective,
  OverlayPosition,
  provideOpenStrategy,
  Select2Component,
  StrategyRelevance,
} from '@core/user-interface-legacy';
import { getInitials, objectEntries, tokenToCssVar } from '@core/utils-legacy';
import type { TellentMemberBrief } from '@tellent-ask-hr/core-types';
import {
  type MemberSelectItem,
  TeamMemberSelectComponent,
} from '@tellent-ask-hr/templates-smart-ui';
import { priorityColorMap } from '@tellent-ask-hr/templates-util';
import type {
  AskHrTicket,
  AskTicketPriority,
  AskTicketStatus,
} from '@tellent-ask-hr/tickets-types';
import { injectStatusTranslations, statusIconMap } from '@tellent-ask-hr/tickets-util';

@Component({
  selector: 'ticket-info-card',
  templateUrl: 'ticket-info-card.component.html',
  styleUrl: './ticket-info-card.component.less',
  imports: [
    TranslatePipe,
    IconComponent,
    ButtonComponent,
    MomentModule,
    AvatarComponent,
    FnCallPipe,
    ButtonDropdownIndicatorDirective,
    DropdownComponent,
    DropdownItemComponent,
    ConnectedOverlayDirective,
    OverlayOriginDirective,
    TeamMemberSelectComponent,
    NgTemplateAssertType,
  ],
  providers: [provideOpenStrategy(InlineOpenStrategy, StrategyRelevance.Custom)],
})
export class TicketInfoCardComponent {
  protected readonly priorityColorMap = priorityColorMap;
  protected readonly priorityTranslations: Record<AskTicketPriority, string> = {
    low: injectTranslate()('tlc.ask_hr.requests.priority_levels.low'),
    normal: injectTranslate()('tlc.ask_hr.requests.priority_levels.normal'),
    high: injectTranslate()('tlc.ask_hr.requests.priority_levels.high'),
    urgent: injectTranslate()('tlc.ask_hr.requests.priority_levels.urgent'),
  };
  protected readonly priorityDropdownItems = objectEntries(this.priorityTranslations).map(
    ([key, value]) => ({
      key,
      value,
      color: this.priorityColorMap[<AskTicketPriority>key],
    }),
  );
  protected readonly statusColorMap: Record<AskTicketStatus, BackgroundToken> = {
    open: 'colorBgAppliedDefault',
    in_progress: 'colorBgInterviewDefault',
    closed: 'colorBgHiredDefault',
  };
  protected readonly ticketStatuses: AskTicketStatus[] = ['open', 'in_progress', 'closed'];
  protected readonly statusIconColorMap: Record<AskTicketStatus, IconToken> = {
    open: 'colorIconDefault',
    in_progress: 'colorIconInfoDefault',
    closed: 'colorIconSuccessDefault',
  };
  protected readonly statusIconMap: Record<AskTicketStatus, IconName> = statusIconMap;
  protected readonly statusTranslations: Record<AskTicketStatus, string> =
    injectStatusTranslations();
  protected readonly tokenToCssVar = tokenToCssVar;
  protected readonly OverlayPosition = OverlayPosition;
  protected readonly MembersSelectType: {
    $implicit: Select2Component<MemberSelectItem>;
    inOverlay: boolean;
  };

  public ticket = input.required<AskHrTicket>();
  public ticketEditable = input<boolean>(true);

  public priorityChanged = output<AskTicketPriority>();
  public statusChanged = output<AskTicketStatus>();
  public assigneesChanged = output<string[]>();
  public cancelClick = output();

  protected requesterInitials = computed(() =>
    getInitials(this.ticket().requester.firstName, this.ticket().requester.lastName),
  );
  protected assigneesControl = new FormControl<string[]>([], { nonNullable: true });

  private setAssignees = effect(() => {
    this.assigneesControl.setValue(this.ticket().assignees.map(assignee => assignee.guid));
  });

  protected assertMemberSelectItem = (item: any): item is MemberSelectItem => item;

  protected onAssigneesChange(assignees: TellentMemberBrief[]) {
    this.assigneesChanged.emit(assignees.map(assignee => assignee.guid));
  }
}
